import React, { useState } from "react";
import styled from "styled-components";
import contactImage from "../assets/contact_us.jpg";
import Header from "../styles/Header";
import { motion } from "framer-motion";
import { FiUser, FiMail, FiMessageSquare, FiSend } from "react-icons/fi";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../authentication/firebase";

const ContactUs = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });

  const [formStatus, setFormStatus] = useState({
    submitted: false,
    error: false,
    loading: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormStatus({ submitted: false, error: false, loading: true });
    
    try {
      // Validate required fields
      if (!formData.name.trim() || !formData.email.trim() || !formData.message.trim()) {
        throw new Error("Please fill in all required fields");
      }

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        throw new Error("Please enter a valid email address");
      }

      // Save to Firebase
      const contactData = {
        name: formData.name.trim(),
        email: formData.email.trim().toLowerCase(),
        subject: formData.subject.trim() || "No subject",
        message: formData.message.trim(),
        createdAt: serverTimestamp(),
        status: "new",
        read: false
      };

      await addDoc(collection(db, "contactMessages"), contactData);
      
      // Show success message
      setFormStatus({ submitted: true, error: false, loading: false });
      
      // Reset form after submission
      setTimeout(() => {
        setFormData({ name: "", email: "", subject: "", message: "" });
        setFormStatus({ submitted: false, error: false, loading: false });
      }, 3000);

    } catch (error) {
      console.error("Error saving contact message:", error);
      setFormStatus({ 
        submitted: false, 
        error: error.message || "Failed to send message. Please try again.", 
        loading: false 
      });
    }
  };

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: "spring",
        stiffness: 100
      }
    }
  };

  return (
    <>
      <Header
        title="Get In Touch"
        description="We're here to help and answer any questions you might have. We look forward to hearing from you!"
      />
      <ContactWrapper>
        <ContactContainer>
          <InfoSection>
            <div className="image-container">
              <img src={contactImage} alt="Contact us" />
            </div>
          </InfoSection>
          
          <FormSection>
            <motion.div 
              className="form-container"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={containerVariants}
            >
              <motion.div className="form-header" variants={itemVariants}>
                <h2>Send Us a Message</h2>
                <p>Fill out the form below and we'll get back to you as soon as possible.</p>
              </motion.div>
              
              {formStatus.submitted ? (
                <motion.div 
                  className="success-message"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 100 }}
                >
                  <div className="success-icon">✓</div>
                  <h3>Thank You!</h3>
                  <p>Your message has been sent successfully. We'll be in touch soon!</p>
                </motion.div>
              ) : formStatus.error ? (
                <motion.div 
                  className="error-message"
                  initial={{ scale: 0.8, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  transition={{ type: "spring", stiffness: 100 }}
                >
                  <div className="error-icon">⚠</div>
                  <h3>Error</h3>
                  <p>{formStatus.error}</p>
                  <button 
                    className="retry-button"
                    onClick={() => setFormStatus({ submitted: false, error: false, loading: false })}
                  >
                    Try Again
                  </button>
                </motion.div>
              ) : (
                <motion.form 
                  onSubmit={handleSubmit}
                  variants={containerVariants}
                >
                  <motion.div className="input-group" variants={itemVariants}>
                    <div className="icon-wrapper">
                      <FiUser />
                    </div>
                    <input
                      type="text"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </motion.div>
                  
                  <motion.div className="input-group" variants={itemVariants}>
                    <div className="icon-wrapper">
                      <FiMail />
                    </div>
                    <input
                      type="email"
                      name="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </motion.div>
                  
                  <motion.div className="input-group" variants={itemVariants}>
                    <div className="icon-wrapper">
                      <FiMessageSquare />
                    </div>
                    <input
                      type="text"
                      name="subject"
                      placeholder="Subject"
                      value={formData.subject}
                      onChange={handleChange}
                    />
                  </motion.div>
                  
                  <motion.div 
                    className="textarea-group" 
                    variants={itemVariants}
                  >
                    <textarea
                      name="message"
                      placeholder="Your Message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                    />
                  </motion.div>
                  
                  <motion.button 
                    className="submit-button" 
                    type="submit"
                    variants={itemVariants}
                    whileHover={{ scale: formStatus.loading ? 1 : 1.05 }}
                    whileTap={{ scale: formStatus.loading ? 1 : 0.95 }}
                    disabled={formStatus.loading}
                  >
                    <span>{formStatus.loading ? "Sending..." : "Send Message"}</span>
                    {formStatus.loading ? (
                      <div className="loading-spinner"></div>
                    ) : (
                      <FiSend />
                    )}
                  </motion.button>
                </motion.form>
              )}
            </motion.div>
          </FormSection>
        </ContactContainer>
      </ContactWrapper>
    </>
  );
};

const ContactWrapper = styled.div`
  width: var(--section-width);
  margin: var(--section-margin) auto;
`;

const ContactContainer = styled.div`
  display: flex;
  border-radius: 24px;
  overflow: hidden;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  height: 650px;
  max-width: 1100px;
  margin: 0 auto;
  background-color: white;
  
  @media (max-width: 1024px) {
    flex-direction: column;
    height: auto;
    max-width: 100%;
  }
`;

const InfoSection = styled.div`
  flex: 1;
  position: relative;
  overflow: hidden;
  
  .contact-methods {
    margin-bottom: 2rem;
    
    .method {
      display: flex;
      align-items: center;
      margin-bottom: 1.5rem;
      
      .icon-circle {
        width: 48px;
        height: 48px;
        border-radius: 50%;
        background-color: rgba(255, 255, 255, 0.2);
        display: flex;
        align-items: center;
        justify-content: center;
        margin-right: 1rem;
        
        svg {
          font-size: 1.25rem;
          color: white;
        }
      }
      
      .method-details {
        h4 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 0.2rem;
          color: white;
        }
        
        p {
          font-size: 0.95rem;
          margin-bottom: 0;
          color: rgba(255, 255, 255, 0.8);
        }
      }
    }
  }
  
  .image-container {
    height: 100%;
    width: 100%;
    
    img {
      width: 100%;
      height: 100%;
      object-fit: cover;
      object-position: center;
    }
  }
  
  @media (max-width: 1024px) {
    height: 250px;
    order: 0;
    
    .image-container {
      img {
        border-radius: 24px 24px 0 0;
        object-position: center top;
      }
    }
  }
  
  @media (max-width: 768px) {
    height: 200px;
    
    .image-container {
      img {
        object-position: center top;
      }
    }
  }
`;

const FormSection = styled.div`
  flex: 1;
  background-color: white;
  
  .form-container {
    padding: 2rem;
    height: 100%;
    display: flex;
    flex-direction: column;
  }
  
  .form-header {
    margin-bottom: 1.5rem;
    
    h2 {
      font-size: 2rem;
      font-weight: 700;
      margin-bottom: 0.75rem;
      color: var(--text-color);
    }
    
    p {
      color: var(--text-gray-color);
      line-height: 1.6;
    }
  }
  
  form {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
  }
  
  .input-group, .textarea-group {
    position: relative;
  }
  
  .input-group {
    display: flex;
    align-items: center;
    background-color: #f8f8f8;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s ease;
    
    &:focus-within {
      box-shadow: 0 0 0 2px var(--primary-color);
      background-color: #fafafa;
    }
    
    .icon-wrapper {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 50px;
      color: var(--text-gray-color);
    }
    
    input {
      flex: 1;
      background: transparent;
      border: none;
      padding: 16px 16px 16px 0;
      font-size: 1rem;
      
      &:focus {
        border: none;
      }
    }
  }
  
  .textarea-group {
    background-color: #f8f8f8;
    border-radius: 12px;
    overflow: hidden;
    transition: all 0.3s ease;
    
    &:focus-within {
      box-shadow: 0 0 0 2px var(--primary-color);
      background-color: #fafafa;
    }
    
    textarea {
      width: 100%;
      height: 150px;
      background: transparent;
      border: none;
      padding: 16px;
      font-size: 1rem;
      resize: none;
      font-family: inherit;
      
      &:focus {
        border: none;
        outline: none;
      }
    }
  }
  
  .submit-button {
    background: linear-gradient(90deg, var(--primary-color) 0%, var(--primary-dark-color) 100%);
    color: white;
    padding: 16px 24px;
    border-radius: 12px;
    font-size: 1rem;
    font-weight: 600;
    border: none;
    cursor: pointer;
    margin-top: 1rem;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    transition: all 0.3s ease;
    
    svg {
      font-size: 1.25rem;
    }

    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
    
    &:hover:not(:disabled) {
      transform: translateY(-2px);
      box-shadow: 0 6px 20px rgba(185, 226, 23, 0.3);
    }

    .loading-spinner {
      width: 20px;
      height: 20px;
      border: 2px solid rgba(255, 255, 255, 0.3);
      border-top: 2px solid white;
      border-radius: 50%;
      animation: spin 1s linear infinite;
    }

    @keyframes spin {
      0% { transform: rotate(0deg); }
      100% { transform: rotate(360deg); }
    }
  }
  
  .success-message {
    background-color: #f0f9e8;
    padding: 2rem;
    border-radius: 16px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    
    .success-icon {
      width: 60px;
      height: 60px;
      background-color: var(--primary-color);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.5rem;
      color: white;
      font-weight: bold;
    }
    
    h3 {
      font-size: 1.5rem;
      color: var(--text-color);
    }
    
    p {
      color: var(--text-gray-color);
    }
  }

  .error-message {
    background-color: #fef2f2;
    padding: 2rem;
    border-radius: 16px;
    text-align: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1rem;
    border: 1px solid #fecaca;
    
    .error-icon {
      width: 60px;
      height: 60px;
      background-color: #ef4444;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.5rem;
      color: white;
      font-weight: bold;
    }
    
    h3 {
      font-size: 1.5rem;
      color: #dc2626;
    }
    
    p {
      color: #7f1d1d;
    }

    .retry-button {
      background-color: #ef4444;
      color: white;
      padding: 8px 16px;
      border-radius: 8px;
      border: none;
      cursor: pointer;
      font-weight: 600;
      transition: background-color 0.2s;

      &:hover {
        background-color: #dc2626;
      }
    }
  }
  
  @media (max-width: 1024px) {
    order: 1;
    
    .form-container {
      padding: 2rem;
    }
  }
`;

export default ContactUs;